import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Search, ChevronLeft, ChevronRight, ShieldAlert } from "lucide-react";
import { api, errMsg, downloadBlob } from "../lib/api";
import { useModule, useApp } from "../context/AppContext";
import { SiteContentEditor } from "./SiteContent";
import { PageHeader, Loading, ErrorBox, EmptyState, Denied, StatusBadge, Btn, fmtDate, fmtVal } from "../components/common";
import RecordForm from "../components/RecordForm";
import RecordDrawer from "../components/RecordDrawer";
import MiniMap from "../components/MiniMap";
import FilterDialog from "../components/FilterDialog";
import { Filter, FileSpreadsheet, FileText, X } from "lucide-react";

function Filters({ module, counts, status, setStatus, q, setQ }) {
  const hidden = module.hidden_statuses || [];
  const total = Object.entries(counts || {}).filter(([k]) => !hidden.includes(k)).reduce((a, [, b]) => a + b, 0);
  const chip = (key, label, n) => (
    <button key={key || "all"} onClick={() => setStatus(key)} data-testid={`status-filter-${key || "all"}`}
      className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${status === key ? "bg-brand border-transparent" : "bg-white hover:bg-slate-50"}`}>
      {label} <span className="font-mono opacity-75">{n}</span>
    </button>
  );
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
      <div className="relative w-full lg:w-80">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference or title…" aria-label="Search records"
          className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm" data-testid="records-search-input" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chip("", "All", total)}
        {module.statuses.map((s) => chip(s.key, s.label, counts?.[s.key] || 0))}
      </div>
    </div>
  );
}

function RecordsTable({ module, items, onOpen }) {
  const cols = module.columns.map((k) => module.fields.find((f) => f.key === k));
  return (
    <div className="overflow-x-auto rounded-md border bg-white" data-testid="data-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 sticky top-0">
          <tr>
            <th className="px-3 py-2.5 font-semibold">Reference</th>
            <th className="px-3 py-2.5 font-semibold">{module.title_label || "Title"}</th>
            {cols.map((c) => <th key={c.key} className="px-3 py-2.5 font-semibold hidden md:table-cell">{c.label}</th>)}
            <th className="px-3 py-2.5 font-semibold">Status</th>
            <th className="px-3 py-2.5 font-semibold hidden lg:table-cell">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(r.id)} tabIndex={0}
              className="cursor-pointer hover:bg-[var(--brand-soft)] focus:bg-[var(--brand-soft)] outline-none" data-testid={`record-row-${r.ref_no}`}>
              <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{r.ref_no}</td>
              <td className="px-3 py-2.5 max-w-xs truncate font-medium">{r.title}</td>
              {cols.map((c) => <td key={c.key} className="px-3 py-2.5 hidden md:table-cell text-slate-600">{r.restricted_fields.includes(c.key) ? "Restricted" : fmtVal(c, r.data[c.key])}</td>)}
              <td className="px-3 py-2.5"><StatusBadge status={r.status} statuses={module.statuses} /></td>
              <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ModulePage() {
  const { key } = useParams();
  const { meta: appMeta } = useApp();
  const user = appMeta?.user;
  const module = useModule(key);
  const [params, setParams] = useSearchParams();
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const fkey = filters.length ? JSON.stringify(filters) : "";
  const openId = params.get("open");

  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [dq, status, key, fkey]);
  useEffect(() => { setQ(""); setStatus(""); setRes(null); setFilters([]); }, [key]);

  const load = useCallback(async () => {
    if (!module || module.special) return;
    setErr("");
    try { setRes((await api.get(`/m/${key}/records`, { params: { q: dq, status, page, size: 20, ...(fkey ? { filters: fkey } : {}) } })).data); } catch (e) { setErr(errMsg(e)); }
  }, [key, dq, status, page, module, fkey]);
  useEffect(() => { load(); }, [load]);

  if (!module || module.special) return <Denied text="This module is not available for your role." />;
  const pages = res ? Math.max(1, Math.ceil(res.total / res.size)) : 1;
  const exportCsv = () => downloadBlob(`/m/${key}/export.csv?status=${status}&q=${encodeURIComponent(dq)}`, `${key}.csv`).then(() => toast.success("Export generated (logged in audit trail)")).catch((e) => toast.error(errMsg(e)));
  const exportReport = (fmt) => {
    const label = module.key === "residents" ? "Inhabitants_Registry" : "Household_Profiling";
    const d = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadBlob(`/m/${key}/report.${fmt}?filters=${encodeURIComponent(fkey)}`, `${label}_Filtered_${d}.${fmt}`).then(() => toast.success("Report generated (logged in audit trail)")).catch((e) => toast.error(errMsg(e)));
  };
  const filtered = !!(fkey && res?.query_text);

  return (
    <div data-testid={`module-page-${key}`}>
      <PageHeader crumbs={[module.group, module.name]} title={module.name}
        subtitle={`Module ${module.no} · Reference prefix ${module.prefix}. Status changes follow the defined workflow and are recorded in history.`}
        actions={<>
          {module.report && filtered && module.can_export && <>
            <Btn variant="outline" onClick={() => exportReport("xlsx")} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4" />Export Excel</Btn>
            <Btn variant="outline" onClick={() => exportReport("pdf")} data-testid="export-pdf-btn"><FileText className="h-4 w-4" />Export PDF</Btn>
          </>}
          {module.report && <Btn variant="outline" onClick={() => setFilterOpen(true)} data-testid="filter-btn"><Filter className="h-4 w-4" />Filter{filters.length ? ` (${filters.length})` : ""}</Btn>}
          {module.can_export && !module.report && <Btn variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download className="h-4 w-4" />Export CSV</Btn>}
          {module.can_create && <Btn onClick={() => setCreating(true)} data-testid="new-record-btn"><Plus className="h-4 w-4" />New record</Btn>}
        </>} />
      {!module.can_view_sensitive && module.fields.some((f) => f.sensitive) && (
        <p className="mb-4 flex items-center gap-2 text-xs text-slate-600 bg-white border rounded-md px-3 py-2" data-testid="sensitive-notice"><ShieldAlert className="h-4 w-4 text-brand2" />Sensitive fields are hidden for your role.</p>
      )}
      {key === "website" && ["super_admin", "captain", "secretary"].includes(user?.role) && <SiteContentEditor />}
      {key === "gis" && res && <div className="mb-5"><MiniMap points={res.items.map((r) => r.data)} /></div>}
      <Filters module={module} counts={res?.status_counts} status={status} setStatus={setStatus} q={q} setQ={setQ} />
      {filtered && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-brand bg-brand-soft px-3 py-2 text-sm" data-testid="filter-query-text">
          <p><b>Active filter:</b> {res.query_text} <span className="text-slate-600">({res.total} match{res.total === 1 ? "" : "es"})</span></p>
          <button onClick={() => setFilters([])} className="flex items-center gap-1 text-xs font-semibold text-brand whitespace-nowrap" data-testid="clear-filters-btn"><X className="h-3 w-3" />Clear filters</button>
        </div>
      )}
      {module.report && <FilterDialog module={module} open={filterOpen} initial={filters} onClose={() => setFilterOpen(false)} onApply={(f) => { setFilters(f); setFilterOpen(false); }} />}
      {err && <ErrorBox text={err} onRetry={load} />}
      {!res && !err && <Loading />}
      {res && res.items.length === 0 && <div className="rounded-md border bg-white"><EmptyState text={module.can_create ? "Create the first record using “New record”." : "Nothing to show for the current filters."} /></div>}
      {res && res.items.length > 0 && <RecordsTable module={module} items={res.items} onOpen={(id) => setParams({ open: id })} />}
      {res && res.total > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600" data-testid="pagination">
          <span>{res.total} record(s) · page {page} of {pages}</span>
          <div className="flex gap-1">
            <Btn variant="outline" className="!px-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></Btn>
            <Btn variant="outline" className="!px-2" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="pagination-next" aria-label="Next page"><ChevronRight className="h-4 w-4" /></Btn>
          </div>
        </div>
      )}
      <RecordForm module={module} open={creating} onClose={() => setCreating(false)} onSaved={load} />
      <RecordDrawer module={module} recordId={openId} onClose={() => setParams({})} onChanged={load} />
    </div>
  );
}
